import { UrlHelper } from './UrlHelper';
import {
  ESessionStore,
  EVariableType,
  ESectionInfoType,
  IdFormat,
  DefaultLanguage,
  EUrlParams,
  ProductSection,
  EPageNames,
  ApplicationSupportedLanguages,
  ETabValue,
  EConfigurationType,
  EVariableAttribute,
  EConfigitProperty,
  ESalesTextType,
  EMessageType,
  RestrictedStates,
  ModelInclusion,
  EConfigitStateCustomProperty,
  EJustification,
} from '../data/Constants';
import { SessionStore } from './SessionStore';
import { AppStore } from '../store';
import { t, TFunction } from 'i18next';
import { isScope, isSection, isSubmodelCountVariable } from './SummaryHelperFunction';
import { countryCodes } from '../localization';
import { appSettings } from '../settings';
import { useAuth } from 'oidc-react';
import { IsDefaultFlowInSessionStore } from './SessionStoreHelperFunctions';
import { resetApplicationSettings } from '../store/states/ApplicationSettingsSlice';
import {
  changeLandingTab,
  getProductImages,
  getPropertyAssociations,
  getSalesText,
  resetConfiguration,
  setOptionalData,
  deleteConfiguration,
  getShortSalesTextAndPrice,
} from '../store/states/ConfigurationSlice';
import { AxiosResponse } from 'axios';
import { resetMyConfigurations, removeConfigurationById } from '../store/states/UserSlice';
import { resetError } from '../store/states/ErrorSlice';
import { clearMessages } from '../store/states/MessagesSlice';
import { NavigateFunction } from 'react-router';
import { ReactElement, createElement } from 'react';
import { AppDispatch } from '../store/AppStore';
import { initiateToast, updateToast } from './ToastHelper';
import { traverseSearchValueOnSections } from './SearchHelperFunctions';

import type {
  IFeatureOrFamily,
  IExtendedConfigureResponse,
  IConfiguration,
  IConfigurationVariable,
  ISection,
  IValueType,
  IVariableAssignment,
  IConfigureRequest,
  IVariableValueAssignment,
  IConfigurationValue,
  IAssignment,
  ISelectableSectionsReturnType,
  IConfigurationProperty,
  IContextData,
  IApplicationSettings,
  IMaterialImages,
  ISubModelDispatchProps,
  IUserPreferences,
  ILanguage,
  IMyConfiguration,
  ISearchDetails,
  IOptionalNumericValue,
} from '../../types';
import { buildAssignmentsByJustification } from './VariableAssignmentsTableHelperFunctions';

/**
 * Retrieves the authentication token and auth object based on the application context.
 * Checks if the app is opened in an iframe, by an external app, or standalone to determine the token source.
 * @returns {{ token: string, auth: any }} An object containing the authentication token and auth object.
 */
export const GetToken = (): { token: string; auth: any } => {
  let token = '';
  let auth: any = null;
  if (IsAppOpenedInIframe()) {
    token = UrlHelper.getSearchParameter(EUrlParams.Token) ?? SessionStore.get(ESessionStore.Token);
  } else if (IsAppOpenedByExtApp()) {
    token = SessionStore.get(ESessionStore.Token);
  } else {
    auth = useAuth();
    token = auth?.userData?.access_token ?? '';
  }
  return { token, auth };
};

/**
 * Checks if a section and all its variables and subsections are guarded.
 * Recursively examines the section to ensure all variables and nested sections have the guarded property.
 * @param {ISection} sec - The section to check.
 * @returns {boolean} True if all variables and subsections are guarded, false otherwise.
 */
export function onlyGuardedVariables(sec: ISection): boolean {
  if (sec.id === ESectionInfoType.Guarded) {
    return false;
  }
  for (const v of sec.variables) {
    if (!v.isGuarded) {
      return false;
    }
  }
  for (const s of sec.sections) {
    if (!onlyGuardedVariables(s)) {
      return false;
    }
  }
  return true;
}

/**
 * Determines the type of the given configuration variable.
 * Checks various properties to classify the variable as submodel, date, number, multiselect, singleselect, or string.
 * @param {IConfigurationVariable} variable - The variable to check.
 * @returns {string} The type of the given variable (e.g., 'Submodel', 'Date', 'Number', 'Multiselect', 'Singleselect', 'String').
 */
export const getVariableType = (variable: IConfigurationVariable | null): string => {
  const hasValues =
    variable?.values && variable.values.length > 0 && variable.values.every((value) => value.name);
  const isSubModel = isSubmodelCountVariable(variable);

  if (isSubModel) {
    return EVariableType.Submodel;
  }
  if (variable?.valueType === 'Date') {
    return EVariableType.Date;
  }
  if (variable?.valueType === 'Number') {
    return EVariableType.Number;
  }
  if (variable?.allowMultipleAssignments) {
    return EVariableType.Multiselect;
  }
  if (hasValues && !variable.allowMultipleAssignments) {
    return EVariableType.Singleselect;
  }
  return EVariableType.String;
};

/**
 * Flattens the hierarchical sections structure into a single-level array.
 * Processes the configuration data to extract all sections, update guarded variables,
 * assign properties, and set completion status.
 * @param {IExtendedConfigureResponse} configuration - The current configuration response.
 * @returns {ISection[]} A flattened list of sections for the current configuration.
 */
export function getFlatSections(configuration: IExtendedConfigureResponse): ISection[] {
  const viewIdSuffix = getViewIdSuffix();
  if (!configuration?.data?.packagePath || !viewIdSuffix) {
    return [];
  }

  const flatSections: ISection[] = [];
  addSectionsToFlatList(configuration.data, flatSections);
  return flatSections;
}

/**
 * Recursively extracts all nested sections into a single flat array.
 * Traverses the section hierarchy and adds each subsection to the provided array.
 * @param {IConfiguration | ISection | undefined} section - The section or configuration to process.
 * @param {ISection[]} flatSections - The array to collect flat sections.
 * @returns {void}
 */
export function addSectionsToFlatList(
  section: IConfiguration | ISection | undefined,
  flatSections: ISection[]
): void {
  if (!section?.sections) {
    return;
  }
  for (const subSection of section.sections) {
    flatSections.push(subSection);
    addSectionsToFlatList(subSection, flatSections);
  }
}

/**
 * Adds isConfigComplete property at the root level.
 * @param {IExtendedConfigureResponse} configuration - The configuration response.
 * @returns {void}
 */
const setIsCompleteConfiguration = (configuration: IExtendedConfigureResponse): void => {
  //Add IsConfigComplete Property at Root level
  if (configuration.data) {
    const viewIdSuffix = getViewIdSuffix();
    configuration.data.isConfigComplete = !configuration.data.sections
      .filter((sec) => !sec.id.endsWith(viewIdSuffix))
      .some((section) => section.isComplete === false);
  }
};

/**
 * Recursively adds the isComplete property to inner sections based on their variables and subsections.
 * Checks if submodels are assigned and validates variables for completion status.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @param {ISection} section - The configuration section to process.
 * @returns {void}
 */
const setIsCompleteToInnerSections = (
  configuration: IExtendedConfigureResponse,
  section: ISection
): void => {
  if (!section || (section.sections.length === 0 && section.variables.length === 0)) {
    return;
  }

  const viewIdSuffix = getViewIdSuffix();

  //Checks if the Submodel is Assigned or not
  if (section.id.endsWith(viewIdSuffix)) {
    const subModels = getSubModelsFromSectionId(section.id);
    if (!isSubModelAssigned(configuration, subModels)) {
      section.isComplete = undefined;
      return;
    }
  }

  //Checks for UnAssigned Variables
  let unAssignedVar = [];
  if (section.variables.length > 0) {
    unAssignedVar = section.variables.filter((variable) => {
      return (
        variable.complete === false && variable.isGuarded !== true && isVariableVisible(variable)
      );
    });
    section.isComplete = unAssignedVar.length === 0;
  }

  //Checks for UnAssigned Sections
  if (section.sections.length > 0) {
    section.sections.forEach((subSection: ISection) => {
      setIsCompleteToInnerSections(configuration, subSection);
    });
    const unAssignedSec = section.sections
      .filter((sec) => !sec.id.endsWith(viewIdSuffix))
      .filter((sec) => {
        return sec.isComplete === false;
      });

    section.isComplete = unAssignedVar.length === 0 && unAssignedSec.length === 0;
  }
};

/**
 * Recursively checks if all sections of a root model are complete, including submodel sections.
 * Returns undefined if section completion status is undefined, false if incomplete, or true if all complete.
 * @param {ISection} section - The configuration section to check.
 * @returns {boolean | undefined} True if all sections are complete, false if any are incomplete, or undefined if status is unknown.
 */
const isAllSectionsComplete = (section: ISection): boolean | undefined => {
  if (section?.isComplete === undefined) {
    return undefined;
  }
  if (section.isComplete === false) {
    return false;
  }
  let isComplete = true;
  if (section.sections.length > 0) {
    for (const subSection of section.sections) {
      if (isAllSectionsComplete(subSection) === false) {
        isComplete = false;
        break;
      }
    }
  }
  return isComplete;
};

/**
 * Checks if the entire configuration is complete.
 * For submodel configurations, checks assigned sections. Unassigned sections are ignored.
 * Loops through all sections to verify completion status.
 * @param {ISection[]} sections - The configuration section list.
 * @returns {boolean} True if the configuration is complete, false otherwise.
 */
export const isConfigurationComplete = (sections: ISection[]): boolean => {
  //loop through all sections and check if each section is complete or not
  if (sections) {
    for (const section of sections) {
      if (isAllSectionsComplete(section) === false) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Evaluates a property across a collection of objects.
 * Provides two evaluation modes: counting items with truthy property values, or checking if any item has a truthy property value.
 *
 * @template T - The type of objects in the collection.
 * @param {T[]} variables - Array of objects to evaluate.
 * @param {keyof T} property - The property key to evaluate on each object (e.g., 'hide', 'suppressed', 'isItemsAvailable').
 * @param {string} type - Evaluation mode: 'int' returns count of items with truthy property; any other value returns boolean.
 * @returns {number | boolean} When type='int': count of items where property is truthy. Otherwise: true if any item has truthy property, false if none.
 *
 * @example
 * // Count hidden variables
 * const hiddenCount = getPropertyCountOrExists<IConfigurationVariable>(variables, 'hide', 'int'); // returns number
 *
 * @example
 * // Check if any section is suppressed
 * const hasSuppressed = getPropertyCountOrExists<ISection>(sections, 'suppressed', 'boolean'); // returns boolean
 */
function getPropertyCountOrExists<T>(variables: T[], property: keyof T, type: string) {
  if (type === 'int') {
    return variables.reduce((count, v) => {
      const value = v[property];
      return count + (value ? 1 : 0);
    }, 0);
  } else {
    return variables.some((variable) => variable[property]);
  }
}

/**
 * Recursively assigns properties to inner sections and their subsections.
 * Updates suppressed, isItemsAvailable, and type properties based on variable states.
 * @param {ISection} section - The section to process.
 * @returns {void}
 */
const assignPropertiesToInnerSections = (
  section: ISection,
  configuration: IExtendedConfigureResponse
): void => {
  if (section?.sections.length > 0) {
    assignPropertiesToSections(section.sections, configuration);
  }
  const suppressedVariableCount = getPropertyCountOrExists<IConfigurationVariable>(
    section.variables,
    EConfigitStateCustomProperty.Suppressed,
    'int'
  );
  const isAllItemHdeOrSuppressed = section.variables.filter((variable) => {
    return !isVariableVisible(variable);
  });

  //update section type to show/hide QTY column  in header
  section.type = getSectionType(section);

  const hiddenVariablesCount = getPropertyCountOrExists<IConfigurationVariable>(
    section.variables,
    EConfigitStateCustomProperty.Hide,
    'int'
  );
  const hiddenInnerSectionsCount = getPropertyCountOrExists<ISection>(
    section.sections,
    EConfigitStateCustomProperty.IsItemsAvailable,
    'int'
  );
  const isSectionSuppressed =
    (suppressedVariableCount === section.variables.length &&
      hiddenInnerSectionsCount === section.sections.length) ||
    (isAllItemHdeOrSuppressed.length === section.variables.length &&
      hiddenInnerSectionsCount === section.sections.length);

  /**
   * Determines if all items (variables and inner sections) in a section are unavailable for display.
   * A section's items are considered unavailable if:
   * 1. All variables have the hide property set to true AND all inner sections have isItemsAvailable set to true, OR
   * 2. The section itself is not suppressed AND all variables are either hidden or suppressed AND
   *    all inner sections are either suppressed or have isItemsAvailable set to true
   * This property is used to determine if a section should be displayed or hidden in the UI.
   */
  section.isItemsAvailable =
    isSectionSuppressed ||
    (hiddenVariablesCount === section.variables.length &&
      hiddenInnerSectionsCount === section.sections.length);

  setIsCompleteToInnerSections(configuration, section);
};

/**
 * Assigns properties (suppressed, isItemsAvailable, type) to all sections in the array.
 * Processes each section to determine if it should be hidden or displayed based on its variables.
 * @param {ISection[]} sections - The list of sections to process.
 * @returns {void}
 */
const assignPropertiesToSections = (
  sections: ISection[],
  state: IExtendedConfigureResponse
): void => {
  sections.forEach((section: ISection) => {
    if (section.sections.length > 0) {
      assignPropertiesToInnerSections(section, state);
    } else {
      // adding suppressed property to sections
      const suppressedVariableCount = getPropertyCountOrExists<IConfigurationVariable>(
        section.variables,
        'suppressed',
        'int'
      );
      //check if all the variables of section are hidden or suppressed
      const isAllItemHdeOrSuppressed = section.variables.filter((variable) => {
        return !isVariableVisible(variable);
      });
      //update section type  if multinumeric sections available in the parent sections array
      section.type = getSectionType(section);

      // adding isItemsAvailable property to sections
      const hiddenVariablesCount = getPropertyCountOrExists<IConfigurationVariable>(
        section.variables,
        EConfigitStateCustomProperty.Hide,
        'int'
      );

      // Returns true if all the variables have HDE=true
      section.isItemsAvailable =
        suppressedVariableCount === section.variables.length ||
        hiddenVariablesCount === section.variables.length ||
        isAllItemHdeOrSuppressed.length === section.variables.length;
      setIsCompleteToInnerSections(state, section);
    }
  });
};

/**
 * Creates a guarded section and adds all guarded variables to it if any exist.
 * Collects all guarded variables from sections and creates a dedicated guarded section.
 * @param {ISection[]} sections - The list of sections to process.
 * @returns {void}
 */
const createGuardedSection = (sections: ISection[]): void => {
  const guardedVariables: IConfigurationVariable[] = [];
  if (sections.length > 0) {
    getGuardedVariables(sections, guardedVariables);
  }
  if (guardedVariables.length > 0) {
    sections.push({
      id: ESectionInfoType.Guarded,
      name: 'Guarded',
      sections: [],
      variables: guardedVariables,
      isComplete: true,
      children: [],
      type: ESectionInfoType.Guarded,
    });
  }
};

/**
 * Recursively collects all guarded variables from sections and their nested subsections.
 * Filters out submodel variables and only includes variables marked as guarded.
 * @param {ISection[]} sections - The list of sections to search.
 * @param {IConfigurationVariable[]} guardedVariables - The array to collect guarded variables.
 * @returns {void}
 */
function getGuardedVariables(
  sections: ISection[],
  guardedVariables: IConfigurationVariable[]
): void {
  sections.forEach((section) => {
    if (section?.sections.length > 0) {
      getGuardedVariables(section.sections, guardedVariables);
    }
    section?.variables?.forEach((vrbl: IConfigurationVariable) => {
      if (vrbl.variableType !== EVariableType.Submodel && vrbl.isGuarded) {
        guardedVariables.push(vrbl);
      }
    });
  });
}

/**
 * Retrieves sections for the currently selected model from flat sections list.
 * Filters sections based on parent hierarchy and view suffix to get relevant sections.
 * @param {ISection[]} flatSections - Extracted flat sections from the configuration.
 * @param {string[]} parents - Array of parent product IDs.
 * @param {string} currentModel - Product ID of the current model.
 * @returns {ISection[]} A filtered list of sections for the currently selected model.
 */
export const getModelSections = (
  flatSections: ISection[],
  parents: string[],
  currentModel: string
): ISection[] => {
  return flatSections.filter((s) => {
    //remove scope section and other unwanted sections from the selectable sections
    if (!s.id || isScope(s) || !isSection(s)) {
      return false;
    }
    const sectionPathParts = s.id?.split('.') || [];
    const view = [...sectionPathParts]
      .reverse()
      .find((sectionPathPart) => sectionPathPart.includes(getViewIdSuffix())) as string;
    //  To resolve inconsistent behavior while loading configureResponse
    const lastView = view ?? '';
    const isSubSection =
      s.id?.substring(s.id?.indexOf(lastView) + lastView.length + 1).split('.').length > 1;
    if (
      lastView?.includes(currentModel) &&
      !s.id?.endsWith(lastView) &&
      !isSubSection &&
      parents.every((p) => s.id?.includes(p)) &&
      s.id?.split(getViewIdSuffix()).length === parents.length + 2
    ) {
      ///section is selectable if last view is for current selected model and
      ///section is not parent section for submodel and
      ///section is not a subsection of and
      ///section id includes every parent id and
      ///section id does not have any other parent id
      return true;
    }

    return false;
  });
};

/**
 * Retrieves selectable sections for the currently selected model based on the configuration and model path.
 * Creates guarded sections for service configurations and performs search traversal.
 * @param {IExtendedConfigureResponse} configuration - The configuration to check.
 * @param {string} [path] - The model path value (optional).
 * @returns {ISelectableSectionsReturnType} An object containing filtered sections, view availability status, search count, and search matches.
 */
export function getSelectableSections(
  configuration: IExtendedConfigureResponse,
  path?: string
): ISelectableSectionsReturnType {
  let searchresult: ISearchDetails = {
    count: 0,
    matches: [],
  };

  if (!configuration?.data?.packagePath || !configuration.savedConfiguration) {
    return {
      sections: [] as ISection[],
      viewUnavailable: false,
      assignmentsByRule: {},
      searchresult: searchresult,
    };
  }

  const { rootModel } = configuration.savedConfiguration.modelContext;
  const modelPath = path ?? UrlHelper.getSearchParameter('model');
  const modelPathParts = modelPath ? [rootModel.id, ...modelPath.split('.')] : [rootModel.id];
  const currentModel = modelPath ? modelPathParts.at(-1)! : rootModel.id;
  const parents = modelPath ? modelPathParts.slice(0, -1) : [];
  const flatSections = getFlatSections(configuration);
  const assignmentsByRule = buildAssignmentsByJustification(
    flatSections,
    configuration,
    EJustification.Rule,
    configuration.numericFallbackAssignments ?? {}
  );

  const viewUnavailable =
    currentModel === rootModel.id
      ? false
      : !flatSections.some((sec) =>
          new RegExp(`${currentModel}${getViewIdSuffix()}$`).exec(sec.id)
        );

  /**
   * We create a deep copy of the sections for the current model as we would need to add a new Section named "Guarded" to show Guarded variables in case IsServiceConfiguration = true.
   * This ensures that any modifications made to the sections for guarded variables do not affect the original section data used elsewhere in the application.
   */
  const sections: ISection[] = JSON.parse(
    JSON.stringify(getModelSections(flatSections, parents, currentModel))
  );

  if (configuration?.savedConfiguration?.isServiceConfiguration) {
    createGuardedSection(sections);
  }

  if (configuration.search.key) {
    searchresult = traverseSearchValueOnSections(
      sections || [],
      configuration.search.key,
      configuration.search.highlightResults
    );
  }

  return {
    sections: sections.filter((sec) => !onlyGuardedVariables(sec)),
    viewUnavailable,
    assignmentsByRule,
    searchresult,
  };
}

/**
 * Recursively searches for a variable by ID within a section and its nested subsections.
 * @param {string} variableId - The variable ID to search for.
 * @param {ISection} section - The section to search in.
 * @returns {IConfigurationVariable | null} The found variable or null if not found.
 */
function getVariableFromSection(
  variableId: string,
  section: ISection
): IConfigurationVariable | null {
  if (section.variables) {
    const variable = section.variables.find((v) => v.id === variableId);
    if (variable) {
      return variable;
    }
  }

  if (section.sections) {
    for (const subSection of section.sections) {
      const result = getVariableFromSection(variableId, subSection);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

/**
 * Retrieves the variable from the last assignment made in the configuration.
 * @param {IExtendedConfigureResponse} configuration - The configuration response.
 * @returns {IConfigurationVariable | null} The variable from the last change or null if not found.
 */
export function getLastAssignmentVariable(
  configuration: IExtendedConfigureResponse
): IConfigurationVariable | null {
  if (!configuration?.data?.sections || !configuration.lastChange) {
    return null;
  }

  return getVariableFromAllSections(configuration, configuration.lastChange.variableId);
}

/**
 * Retrieves a variable by its ID from all sections in the configuration response.
 * Loops through all top-level sections and recursively searches for the variable.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @param {string} variableId - The variable ID to search for.
 * @returns {IConfigurationVariable | null} The found variable or null if not found.
 */
export function getVariableFromAllSections(
  configuration: IExtendedConfigureResponse,
  variableId: string
): IConfigurationVariable | null {
  if (!configuration?.data?.sections) {
    return null;
  }
  for (const section of configuration.data.sections) {
    const result = getVariableFromSection(variableId, section);
    if (result) {
      return result;
    }
  }
  return null;
}

/**
 * Generates the display name for a feature or family with or without its code.
 * Uses short sales text if configured, and formats based on page type and showCode preference.
 * @param {IFeatureOrFamily} feature - The feature or family object.
 * @param {boolean} [showCode=true] - Whether to show the feature code.
 * @param {boolean} [pageType] - Whether this is for a summary page (changes format).
 * @returns {string} The formatted name with or without code based on showCode preference.
 */
export function getNameWithCode(
  feature: IFeatureOrFamily,
  showCode: boolean = true,
  pageType?: boolean
): string {
  const id = getFeatureCode(feature.id ?? '', feature.variableType ?? '');
  const featureName =
    appSettings.UseShortSalesText && feature.shortSalesText ? feature.shortSalesText : feature.name;

  //If Quick summary or detailed summary page returns the featureCode-FeatureName  format
  // Else returns featureName(featureCode) format
  const variableName = pageType
    ? `${id || feature.value} - ${featureName}`
    : `${featureName} (${id || feature.value})`;
  return showCode ? `${variableName}` : `${featureName}`;
}

/**
 * Extracts the feature code from a variable ID, removing internal hierarchy and suffixes.
 * For submodels, removes the count variable suffix (_C).
 * @param {string} variableId - The variable ID.
 * @param {string} variableType - The variable type.
 * @returns {string} The feature code without hierarchy (e.g., "867043" from "I_867030.867043" or "866060" from "866060_C").
 */
export function getFeatureCode(variableId: string, variableType: string): string {
  let id = variableId;
  //To get the actual feature instead of whole heirarchy
  //Ex: id = I_867030.867043 ---> 867043
  id = id?.substring(id.lastIndexOf('.') + 1);
  if (variableType === EVariableType.Submodel) {
    //When it's a submodel, remove the last two letters for the count variable
    //Ex: 866060_C ---> 866060
    id = id ? id.slice(0, -2) : id;
  }
  return id;
}

/**
 * Retrieves the current view ID from session storage or configuration.
 * Falls back to default view or first available view if not set.
 * @returns {string} The current view ID (e.g., "CPQ", "ESTORE") or "system" as fallback.
 */
export function getViewId(): string {
  const viewId = SessionStore.get(ESessionStore.ViewId);
  const config: IExtendedConfigureResponse = AppStore.getState().configuration;
  const defaultView = config?.viewIds ? config?.viewIds[0] : null;
  if (viewId) {
    // if viewId already set
    return viewId;
  } else if (defaultView) {
    // to set default viewId if available
    SessionStore.set(ESessionStore.ViewId, defaultView);
    return defaultView;
  } else if (config.viewIds && config.viewIds.length > 0) {
    // setting the first available view if default view not available
    SessionStore.set(ESessionStore.ViewId, config.viewIds[0]);
    return config.viewIds[0];
  } else {
    return 'system';
  }
}

/**
 * Parses the package path from the configuration store into its type and code parts.
 * Package path format: "C{packageCode}/{packageType}{packageCode}~{guid}" (e.g. "C782199/R782199~ab8c8984-...").
 * @returns {{ packageCode: string, packageType: string } | null} The parsed package details, or null if unavailable or malformed.
 */
export function getPackageDetails(packagePath: string): {
  packageCode: string;
  packageType: string;
} {
  const [pathPart, typePart] = packagePath.split('/');

  const packageCode = pathPart.slice(1);
  const packageType = typePart.charAt(0);

  return {
    packageType,
    packageCode,
  };
}

/**
 * Extracts submodel IDs from a section ID by parsing view suffix parts.
 * @param {string} sectionId - The complete section ID.
 * @returns {string[]} An array of submodel IDs extracted from the section ID.
 */
export function getSubModelsFromSectionId(sectionId: string): string[] {
  return sectionId
    .split('.')
    .filter((sIdPart) => sIdPart.endsWith(getViewIdSuffix()))
    .slice(1)
    .map((sIdPart) => sIdPart.replace(getViewIdSuffix(), ''));
}

/**
 * Checks if all submodel variables in the path are activated (COUNT variable set to 1 or higher).
 * Validates that each submodel in the hierarchy has been assigned.
 * @param {IExtendedConfigureResponse} configuration - The configuration object.
 * @param {string[]} subModels - An array of the submodel path.
 * @returns {boolean} True if all submodels are active (assigned), false otherwise.
 */
export function isSubModelAssigned(
  configuration: IExtendedConfigureResponse,
  subModels: string[]
): boolean {
  const countVariables = configuration.contextData?.countVariables || {};
  let previousPath = '';
  for (const subModel of subModels) {
    const varPath = `${previousPath}${subModel}${IdFormat.Suffix.SubmodelId}`;
    const assignment = countVariables[varPath]?.values.find((v) => v.assigned);
    if (!assignment || Number(assignment.value) < 1) {
      return false;
    }
    previousPath += `I_${subModel}.`;
  }
  return true;
}

/**
 * Sets the complete property on a variable based on its assignment status.
 * For password-protected, number, and submodel variables, checks if any value is assigned.
 * For string variables, ensures the value is non-empty and assigned.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {void}
 */
const setIsCompleteToVariables = (
  variable: IConfigurationVariable,
  optionalItems?: Map<string, boolean | IOptionalNumericValue>
): void => {
  let assigned;
  if (variable.variableType === EVariableType.String && checkIsPasswordFeature(variable)) {
    assigned = variable.values?.find((v) => v.assigned);
    variable.complete = !!assigned;
  } else if (variable.valueType === EVariableType.Number) {
    assigned = variable.values?.find((v) => v.assigned);
    if (assigned) {
      variable.complete = true;
    } else {
      // A numeric variable that is not yet assigned is still complete if the user
      // has explicitly added it as optional — it will be included in the order as optional.
      variable.complete = optionalItems?.has(variable.id) ?? false;
    }
  } else if (variable.variableType === EVariableType.String) {
    //Check the string feature value is assigned or not
    //The value should not be empty and isAssigned flag is true
    assigned = variable.values?.find((v) => v.value && v.assigned);
    variable.complete = !!assigned;
  } else if (
    variable.variableType === EVariableType.Multiselect ||
    variable.variableType === EVariableType.Singleselect
  ) {
    variable.complete = variable.values?.every((v) => v.complete);
  }
};

/**
 * Updates price and short sales text properties for single-select and multi-select variable options.
 * Checks price relevance and bundled status for each option value.
 * @param {IConfigurationVariable} variable - The variable to update.
 * @param {string} variableType - The variable type.
 * @param {IExtendedConfigureResponse} config - The configuration object.
 * @returns {void}
 */
const setPropertiesToSingleAndMultiValuedOptions = (
  variable: IConfigurationVariable,
  config: IExtendedConfigureResponse
): void => {
  variable.values.forEach((val) => {
    val.complete = isLessFeature(String(val.value)) ? Boolean(!val?.assigned) : true;
    //If IPR property is true then return the price value else returns null
    //If Price is not null or undefined then returns null
    const isPriceRelevant = val.pdmProperties ? val.pdmProperties.priceRelevant : false;
    val.price = null;
    if (isPriceRelevant) {
      const valuePrice: number | null = config.price
        ? getValueById(config.price, val.value.toString())
        : null;
      val.price = valuePrice;
    }
    const drivenCode = variable.id + IdFormat.CodeSeparator + String(val.value);
    const drivingCode = getBundleDrivingCodeFromDrivenCode(config, drivenCode);
    if (drivingCode) {
      val.bundleInfo = { isBundled: true, bundleDrivingCode: drivingCode };
      if (isPriceRelevant) {
        val.price = 0;
      }
    }
    // To assign shortSalesText data to the features of SVFF & MVFF
    val.shortSalesText =
      (appSettings.UseShortSalesText &&
        config.shortSalesText &&
        getValueById(config.shortSalesText, val.value.toString())) ||
      undefined;
  });
};

/**
 * Sets the suppressed and hide properties on variables based on RMA, PMA, and HDE properties.
 * Updates variable-level suppression based on market availability.
 * For multi/single-select variables, also updates suppression on individual options.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {void}
 */
const setSuppressedToVariables = (variable: IConfigurationVariable): void => {
  variable.suppressed = !isFeatureValidByMarketAvailabilty(variable);

  //add hide property to the variable and values
  variable.hide = !!getPropertyValue<boolean>(
    variable,
    EConfigitProperty.Hide,
    EVariableType.Boolean
  );
  AddAttributeToValue(variable, EVariableAttribute.Hide);

  //update variable  hide property to true if all the values are hide
  if (!variable.hide && variable.values.length > 0) {
    const hideValues = variable.values.filter((val) => val.hide);
    variable.hide = hideValues.length === variable.values.length;
  }
};

/**
 * Creates a configure request object for the Configit API.
 * Includes product ID, configuration ID, view ID, language, currency, and variable assignments.
 * @param {string} productId - The product ID.
 * @param {string | null} configurationId - The configuration ID (can be null for new configurations).
 * @param {IAssignment[] | null} variableAssignments - The current variable assignments.
 * @returns {IConfigureRequest} The configure request object ready for API submission.
 */
export function createConfigureRequest(
  productId: string,
  configurationId: string | null,
  variableAssignments: IAssignment[] | null
): IConfigureRequest {
  const view: string = getViewId();
  const langCode = getLanguageCode();
  const configureSetting: IConfigureRequest['settings'] = {
    debug: false,
    includePriceLines: false,
    phaseBehavior: 'InSections',
    assignmentResolutionOrder: 'ByPriority',
    includeStateAndJustification: true,
  };
  const currency = AppStore.getState().applicationSettings.currency;
  return {
    configureRequest: {
      configurationId: configurationId ?? '',
      globalArguments: {
        SalesArea: {
          SalesOrganization: '0001',
          DistributionChannel: '01',
          SalesDivision: '01',
        },
      },
      viewId: view,
      line: {
        quantity: {
          unit: 'EA',
          value: 1,
        },
        productId: productId,
        priceLineAssignments: [],
        variableAssignments: variableAssignments || [],
        arguments: {},
        sublines: [],
      },
      settings: configureSetting,
      currency: currency.countryCode ? currency.countryCode : 'EUR',
      date: '2021-09-15T10:53:44.173Z',
      language: langCode,
    },
  };
}

/**
 * Generates a configure request object from the current configuration state.
 * Optionally removes conflicted assignments before creating the request.
 * @param {IExtendedConfigureResponse} configuration - The current configuration.
 * @param {boolean} [removeConflicts=false] - Whether to remove conflicted assignments.
 * @returns {IConfigureRequest} The configure request object.
 */
export function getConfigureRequest(
  configuration: IExtendedConfigureResponse,
  removeConflicts: boolean = false
): IConfigureRequest {
  //If Conflicts exists, remove the conflicted assignments from configure request object
  if (removeConflicts) {
    configuration.conflict?.assignmentsToRemove.conflicts.forEach((v) => {
      const index = configuration.assignments?.findIndex(
        (assignment) =>
          assignment.variableId === v.variable.id && assignment.value === v.value.value
      );
      if (index && index >= 0) {
        configuration.assignments?.splice(index, 1);
      }
    });
  }
  const currentAssignments = configuration.assignments ? [...configuration.assignments] : [];
  return createConfigureRequest(
    configuration.savedConfiguration.modelContext.rootModel.id,
    configuration.configurationId,
    currentAssignments
  );
}

/**
 * Extracts the model ID of a submodel from its section ID.
 * Removes the view suffix to get the clean submodel ID.
 * @param {string} id - The complete ID of the submodel section.
 * @returns {string} The model ID of the submodel, or empty string if not a submodel section.
 */
export const getSubmodelIdFromSectionId = (id: string): string => {
  if (!id.endsWith(getViewIdSuffix())) {
    return '';
  }
  return id?.split('.')?.pop()?.replace(getViewIdSuffix(), '') ?? '';
};

/**
 * Returns an array of names representing values that were removed from assignment.
 * Filters out excluded values for multi-valued variables.
 * @param {IVariableValueAssignment[]} values - The values that are currently removed from assignment.
 * @param {boolean} [isMulti] - Flag indicating if variable is multi-valued.
 * @param {boolean} [showCode] - Whether to show feature codes in the names.
 * @returns {string[]} Array of formatted names for removed values.
 */
export const getRemovedValues = (
  values: IVariableValueAssignment[],
  isMulti?: boolean | null,
  showCode?: boolean
): string[] =>
  values
    .filter((value: IVariableValueAssignment) => !isMulti || !value.exclude)
    .map((value: IVariableValueAssignment) => getNameWithCode(value as IFeatureOrFamily, showCode));

/**
 * Generates a human-readable description of variable changes.
 * Constructs a message showing what was unassigned, cleared, or changed.
 * @param {IVariableValueAssignment[]} values - The values that were removed from assignment.
 * @param {boolean} [isMulti] - Flag indicating if variable is multi-valued.
 * @param {number} [changeType] - Integer indicating the type of change (0=unassigned, 1=cleared, 2=changed).
 * @param {boolean} [showCode] - Whether to show feature codes.
 * @param {IConfigurationValue[]} [assigned] - The values that will be assigned (for changeType 2).
 * @returns {string[]} Array of strings forming the change description.
 */
export const getChanges = (
  values: IVariableValueAssignment[],
  isMulti?: boolean | null,
  changeType?: number,
  showCode?: boolean,
  assigned?: IConfigurationValue[]
): string[] => {
  const removed: IVariableValueAssignment[] = values.filter(
    (value: IVariableValueAssignment) => !isMulti || !value.exclude
  );
  const ct: number = changeType === 1 || changeType === 2 ? changeType : 0;
  const connectingStatement = ['unassigned', 'cleared', 'changed'].map((key: string) =>
    t('configurationValidationDialog.getChanges.' + key)
  );
  const res: string[] = [
    removed
      .map((value: IVariableValueAssignment) =>
        getNameWithCode(value as IFeatureOrFamily, showCode)
      )
      .join(',') + ' ' || '',
    (removed.length > 1 ? 'are ' : 'is ') + connectingStatement[ct],
  ];
  if (changeType === 2) {
    res.push(
      ' ' +
        assigned
          ?.map((value: IConfigurationValue) =>
            getNameWithCode(value as IFeatureOrFamily, showCode)
          )
          .join(',') || ''
    );
  }
  return res;
};

/**
 * Extracts the view ID suffix from the selected view ID.
 * Removes the product ID prefix to get only the suffix portion.
 * @returns {string} The view ID suffix (e.g., "_CPQ", "_ESTORE") or empty string if not available.
 */
export const getViewIdSuffix = (): string => {
  const viewId: string = SessionStore.get(ESessionStore.ViewId);
  const productId: string = getProductId();
  if (viewId && viewId.length > 0 && productId) {
    return viewId.slice(productId.length);
  }
  return '';
};

/**
 * Retrieves the product ID from session storage.
 * @returns {string} The product ID or empty string if not found.
 */
export const getProductId = (): string => {
  const productId: string = SessionStore.get(ESessionStore.ProductId);
  if (productId && productId.length > 0) {
    return productId;
  }
  return '';
};

/**
 * Determines the language code to use after a preference change.
 * Matches user preference against available languages by code or name.
 * @param {IUserPreferences} userPreferences - The user preferences object.
 * @param {ILanguage[]} availableLanguages - The list of available languages.
 * @returns {string} The language code or empty string if no match found.
 */
export const getLangAfterPrefChange = (
  userPreferences: IUserPreferences,
  availableLanguages: ILanguage[]
): string => {
  const langCode = userPreferences
    ? availableLanguages.filter((lang: ILanguage) => {
        return lang.code === userPreferences.language || lang.name === userPreferences.language;
      })
    : [];
  return langCode?.length === 0 ? '' : langCode[0]['code'];
};

/**
 * Retrieves the appropriate language code based on context and user settings.
 * Handles URL parameters, user settings, user preferences, and defaults.
 * Uses different language sets for landing page vs configuration pages.
 * @param {string} [location] - The current page location.
 * @param {boolean} [isPrefLangChanged] - Whether language preference has changed.
 * @returns {string} The language code to use (e.g., "en-US", "de-DE").
 */
export const getLanguageCode = (location?: string, isPrefLangChanged?: boolean): string => {
  const userSettings = AppStore.getState().userSettings;
  const userPreferences = AppStore.getState().userPreferences;
  const configuration = AppStore.getState().configuration;
  let langCode: ILanguage[];
  let availableLanguages = configuration['pmLanguages'] ?? DefaultLanguage.Value;
  if (
    location === EPageNames.LandingPage ||
    location === EPageNames.Logout ||
    location === EPageNames.CloseTab ||
    location === EPageNames.Notified
  ) {
    availableLanguages = ApplicationSupportedLanguages;
  }
  if (!IsDefaultFlowInSessionStore()) {
    //To handle when language is passed from down-stream application
    const urlLanguage = SessionStore.get(ESessionStore.Language);
    const urlCountry = SessionStore.get(ESessionStore.Country);
    const urlLanguageCode = urlLanguage + '-' + urlCountry; // Concatenate the language and country to get the locale code
    const isValidLanguage = Object.values(countryCodes)
      .map((x) => x.locale)
      .includes(urlLanguageCode);
    const isUrlLanguageAvailable = availableLanguages
      .map((lang) => lang.code)
      .includes(urlLanguageCode);
    if (isValidLanguage && isUrlLanguageAvailable) {
      return urlLanguageCode;
    }
  }
  langCode = userSettings
    ? availableLanguages.filter((lang: ILanguage) => {
        return lang.code === userSettings.language || lang.name === userSettings.language;
      })
    : [];
  if (userPreferences.preferenceChanged && isPrefLangChanged) {
    return getLangAfterPrefChange(userPreferences, availableLanguages);
  }
  return langCode?.length === 0 ? DefaultLanguage.Id : langCode[0]?.code;
};

/**
 * Checks if a feature or family can be marked as optional.
 * Looks for the CANBEOPTIONAL (IsOptional) property in the feature's properties.
 * @param {IConfigurationProperty[]} properties - The properties of the feature or family.
 * @returns {any} The CANBEOPTIONAL property value (typically boolean).
 */
export const canBeOptional = (properties: IConfigurationProperty[]): any => {
  return properties?.find(
    (property: IConfigurationProperty) => property.id === EConfigitProperty.IsOptional
  )?.value;
};

/**
 * Creates a concatenated ID combining feature family and feature IDs.
 * For multi/single-select variables, concatenates variable.id with feature ID using dot notation.
 * For other types, returns the feature ID as-is.
 * @param {string} featureId - The ID of the feature (for multi/single-select) or family (for string/numeric).
 * @param {IConfigurationVariable} variable - The feature family object.
 * @returns {string} The concatenated ID (e.g., "familyId.featureId") or just featureId.
 */
export const createId = (featureId: string, variable: IConfigurationVariable): string => {
  return variable?.variableType === EVariableType.Multiselect ||
    variable?.variableType === EVariableType.Singleselect
    ? variable.id + '.' + featureId
    : featureId;
};

/**
 * Creates a feature ID based on variable type.
 * For multi/single-select variables, returns the value as the feature ID.
 * For other types (string, numeric), returns the variable ID as the feature family ID.
 * @param {IValueType} value - The configuration value.
 * @param {IConfigurationVariable | null} variable - The configuration variable.
 * @returns {string} The feature or family ID.
 */
export const getFeatureId = (
  value: IValueType,
  variable: IConfigurationVariable | null
): string => {
  return String(
    variable?.variableType === EVariableType.Multiselect ||
      variable?.variableType === EVariableType.Singleselect
      ? value
      : variable?.id
  );
};

/**
 * Checks if a feature or family is marked as optional in the configuration state.
 * @param {string} featureId - The ID of the feature (for multi/single-select) or family (for string/numeric).
 * @param {IConfigurationVariable} variable - The feature family object.
 * @returns {boolean} True if the item is in the optional items collection, false otherwise.
 */
export const checkIsMarkedOptional = (
  featureId: string,
  variable: IConfigurationVariable,
  config?: IExtendedConfigureResponse
): boolean => {
  const id = createId(featureId, variable);
  const configuration = config ?? AppStore.getState().configuration;
  return configuration.savedConfiguration?.optionalItems?.has(id) ?? false;
};

/**
 * Updates the optional items state by adding or removing a feature/family.
 * Creates or updates the optionalItems Map in the Redux store.
 * @param {string} featureId - The ID of the feature (for multi/single-select) or family (for string/numeric).
 * @param {boolean} value - True to mark as optional, false to unmark.
 * @param {IConfigurationVariable} variable - The feature family object.
 * @returns {Map<string, boolean>} The updated optional items Map.
 */
export const setOptionalItemsToState = (
  featureId: string,
  value: boolean | IOptionalNumericValue,
  variable: IConfigurationVariable,
  optionalItemsCopy?: Map<string, boolean | IOptionalNumericValue>
): Map<string, boolean | IOptionalNumericValue> => {
  const id = createId(featureId, variable);
  const optionalItems = optionalItemsCopy ?? new Map<string, boolean | IOptionalNumericValue>();
  if (value) {
    optionalItems.set(id, value);
  } else {
    optionalItems.delete(id);
  }
  AppStore.dispatch(setOptionalData({ optionalItems: optionalItems }));
  return optionalItems;
};

/**
 * Checks if a feature or family has an assigned value.
 * For numeric/string variables, checks if value is non-zero/non-empty and assigned.
 * For multi/single-select variables, checks if the specific option is assigned.
 * @param {string} featureOrOptionId - The ID of the feature or option to check.
 * @param {IConfigurationVariable} variable - The feature family object.
 * @returns {boolean} True if the feature or option has an assigned value, false otherwise.
 */
export const checkIsFeatureSelected = (
  featureOrOptionId: string,
  variable: IConfigurationVariable
): boolean | undefined => {
  const variableType = getVariableType(variable);

  if (variableType === EVariableType.Number || variableType === EVariableType.String) {
    return variable.values.some(
      (value: IConfigurationValue) => value.value && value.assigned && value.value !== 0
    );
  }

  // For VariableType other than Number / String like MultiValued, SingleValued, MultiNumeric
  const value = variable?.values?.find((v) => v.value === featureOrOptionId);
  return !!value?.assigned;
};

/**
 * Parses a section ID to determine the model and section type.
 * Identifies scope sections, model sections, multi-numeric sections, and regular sections.
 * @param {string} [sectionId] - The ID of the section to be checked.
 * @returns {{ model: string, type: ESectionInfoType }} An object with the model ID and section type.
 */
export function getSectionInfo(sectionId?: string): { model: string; type: ESectionInfoType } {
  if (!sectionId) {
    return { model: '', type: ESectionInfoType.Invalid };
  }

  //get the ViewId CPQ or ESTORE
  const viewIdSuffix = getViewIdSuffix();

  //get the sectionId
  //ex:95211_CPQ.Scope571 --> Scope571
  const id = sectionId.split('.').pop();
  if (id?.match(IdFormat.Regex.ScopeSection)) {
    return { model: '', type: ESectionInfoType.Scope };
  }

  //check whether the section id contains view name  or not
  if (id?.endsWith(viewIdSuffix)) {
    return { model: id?.replace(viewIdSuffix, '') || '', type: ESectionInfoType.Model };
  }

  // check whether the section id contains S or not to identify its valid section.
  if (id?.startsWith(IdFormat.Suffix.Section)) {
    const match = new RegExp(IdFormat.Regex.Section).exec(id);
    if (match && id.split('_').length > 2) {
      return { model: id, type: ESectionInfoType.MultiNumeric };
    } else if (match) {
      return { model: id.slice(1, match.index), type: ESectionInfoType.Section };
    }
  }
  return { model: '', type: ESectionInfoType.Invalid };
}

/**
 * Validates if a section is a valid configurable section.
 * Excludes hidden sections and scope sections. Validates section type and model availability.
 * @param {IContextData | null} contextData - The context data required for validation.
 * @param {ISection} section - The section to be validated.
 * @returns {boolean} True if the section is a valid configurable section, false otherwise.
 */
export function isValidConfigurableSection(
  contextData: IContextData | null,
  section: ISection
): boolean {
  //Return False if _HIDDEN_ present in the  section id
  if (!section.id || section.id?.endsWith(ProductSection.HiddenSection)) {
    return false;
  }
  let res = true;
  const models = contextData?.models;

  //check whether the sections are configurable by section type
  // remove scope and hidden section because thease are not configurable
  section.id.split('.').forEach((idPart) => {
    const info = getSectionInfo(idPart);
    if (!(
      info.type === ESectionInfoType.MultiNumeric ||
      info.type === ESectionInfoType.Scope ||
      (models && info.model in models && info.type !== ESectionInfoType.Invalid)
    )) {
      res = false;
    }
  });
  return res;
}

/**
 * Checks if an option or variable has the ReadOnly property set to true.
 * @param {IConfigurationValue | IConfigurationVariable} value - The value or variable containing properties.
 * @returns {boolean} True if the ReadOnly property is present and true, false otherwise.
 */
export function checkIsOptionReadonly(
  value: IConfigurationValue | IConfigurationVariable
): boolean {
  return !!value.properties?.some(
    (property: IConfigurationProperty) =>
      property.id === EConfigitProperty.ReadOnly && property.value
  );
}

/**
 * Checks if an option or variable has the Hide property set to true.
 * @param {IConfigurationValue | IConfigurationVariable} value - The value or variable containing properties.
 * @returns {boolean} True if the Hide (HDE) property is present and true, false otherwise.
 */
export function checkIsOptionHidden(value: IConfigurationValue | IConfigurationVariable): boolean {
  return !!value.properties?.some(
    (property: IConfigurationProperty) => property.id === EConfigitProperty.Hide && property.value
  );
}

/**
 * Checks if a variable is suppressed based on market availability.
 * @param {IConfigurationVariable} value - The configuration variable.
 * @returns {boolean} True if the variable is suppressed, false otherwise.
 */
export function isSuppressed(
  value: IConfigurationVariable | IConfigurationValue
): boolean | undefined {
  return value?.suppressed;
}

/**
 * Formats a price value according to the current currency locale settings.
 * Uses the application's currency configuration for locale-specific formatting.
 * @param {number | null} price - The price value to format.
 * @returns {string} The formatted price string with locale-specific number formatting.
 */
export function getFormattedPrice(price: number | null): string {
  const currency = AppStore.getState().applicationSettings.currency;
  return (
    ' ' +
    price?.toLocaleString('' + currency.locale + '-u-nu-latn', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })
  );
}

/**
 * Retrieves material images for a feature/family or submodel from the configuration state.
 * For submodel count variables, returns submodel-specific images.
 * @param {IFeatureOrFamily} variable - The feature family or submodel.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @returns {IMaterialImages[] | undefined} Array of material images or undefined if not found.
 */
export function getImages(
  variable: IFeatureOrFamily,
  configuration: IExtendedConfigureResponse
): IMaterialImages[] | undefined {
  const isVariableSubmodel = isSubmodelCountVariable(variable);
  const currentModel = isVariableSubmodel
    ? variable.id?.split('.')?.pop()?.split('_')[0]
    : getCurrentModel(configuration);
  const productImages = configuration.productImages?.get(currentModel);
  const variableId = variable.id ? variable.id?.split('.').pop() : variable.value;
  const id = isVariableSubmodel ? currentModel : variableId;
  const images = variableId ? productImages?.get(id)?.materialImages : undefined;

  return images;
}

/**
 * Checks if the application is opened within an iframe.
 * Compares window location with parent window location.
 * @returns {boolean} True if the app is running in an iframe, false otherwise.
 */
export function IsAppOpenedInIframe(): boolean {
  return globalThis.location !== globalThis.parent.location;
}

/**
 * Retrieves the scope key from URL parameters or session storage.
 * @returns {string | null} The scope key value or null if not found.
 */
export function getScopeKey(): string | null {
  return UrlHelper.getSearchParameter(EUrlParams.Scope) ?? SessionStore.get(ESessionStore.Scope);
}

/**
 * Checks if the application was opened by an external application.
 * Verifies the presence of a token in session storage.
 * @returns {boolean} True if opened by an external app, false otherwise.
 */
export function IsAppOpenedByExtApp(): boolean {
  return !!SessionStore.get(ESessionStore.Token);
}

/**
 * Determines if the application should display header and footer.
 * Checks session storage for headerless mode setting.
 * @returns {boolean} True if header/footer should be hidden, false if they should be shown.
 */
export function showHeaderFooter(): boolean {
  return SessionStore.get(ESessionStore.HeaderLess)
    ? JSON.parse(SessionStore.get(ESessionStore.HeaderLess))
    : false;
}

/**
 * Navigates to the home/landing page and resets all application state.
 * Clears configuration, settings, and session data before navigation.
 * Triggered when clicking on the Home icon or Philips logo.
 * @param {NavigateFunction} navigate - React Router navigate function.
 * @returns {void}
 */
export const navigateToHome = (navigate: NavigateFunction) => {
  AppStore.dispatch(resetConfiguration());
  AppStore.dispatch(resetApplicationSettings());
  AppStore.dispatch(resetMyConfigurations());
  AppStore.dispatch(changeLandingTab({ value: true, tabValue: ETabValue.ProductPortfolio }));
  AppStore.dispatch(resetError());
  AppStore.dispatch(clearMessages());
  SessionStore.remove(ESessionStore.ViewId);
  SessionStore.remove(ESessionStore.PriceListMap);

  navigate(EPageNames.LandingPage, { replace: true });
};

/**
 * Processes successful API responses and returns the data.
 * Standard success handler for Axios responses.
 * @param {AxiosResponse} response - The Axios response object.
 * @returns {{ data: any }} Object containing the response data.
 */
export function handleSuccess(response: AxiosResponse): { data: any } {
  return {
    data: response.data,
  };
}

/**
 * Removes hierarchy and view suffix from a product code.
 * Extracts the clean product code by removing prefixes and suffixes.
 * @param {string} id - The full product ID with hierarchy and view suffix.
 * @returns {string} The cleaned product code.
 */
export function removeViewSuffix(id: string): string {
  id = id.substring(id.lastIndexOf('.') + 1);
  return id.substring(0, id.length - getViewIdSuffix().length);
}

/**
 * Determines if action buttons should be disabled based on configuration state and access level.
 * Checks for restricted states and read-only access.
 * @returns {boolean} True if action buttons should be disabled, false otherwise.
 */
export function disableActionButton(): boolean {
  const configuration = AppStore.getState().configuration;
  const savedState = configuration.savedConfiguration?.state;
  const accessState = configuration.access;
  return (
    isRestrictedState(savedState ?? '') ||
    isReadOnlyAccess(accessState) ||
    isTempConfiguration(configuration.savedConfiguration?.state ?? '')
  );
}

/**
 * Determines if the configuration summary tab drawer should be displayed.
 * Checks page size and summary display settings.
 * @param {IApplicationSettings} applicationSettings - The application settings.
 * @returns {boolean} True if the summary drawer should be shown, false otherwise.
 */
export function showConfigurationSummaryTabDrawer(
  applicationSettings: IApplicationSettings
): boolean {
  return (
    (!applicationSettings.pageSize.isMedium && applicationSettings.showSummary) ||
    (applicationSettings.pageSize.isMedium && applicationSettings.forceShowSummary)
  );
}

/**
 * Checks if a variable has compatible (valid) unassigned values.
 * Only applies to Number and Submodel variable types.
 * @param {IConfigurationVariable} variable - The configuration variable to check.
 * @returns {boolean} True if compatible values exist, false otherwise.
 */
export function hasCompatibleValues(variable: IConfigurationVariable): boolean {
  if (
    variable.variableType !== EVariableType.Number &&
    variable.variableType !== EVariableType.Submodel
  ) {
    return false;
  }
  if (variable.values.length === 1) {
    return !variable.values[0].incompatible;
  } else {
    return variable.values.some(
      (value: IConfigurationValue) => !value.assigned && !value.incompatible
    );
  }
}

/**
 * Validates a feature based on RMA and PMA (market availability) properties.
 * Checks different conditions based on variable type (String, Number, Submodel, Multiselect, etc.).
 * Returns true if the feature is valid for the current market, considering assigned values.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {boolean | undefined} True if valid by market availability, false if invalid, undefined if indeterminate.
 */
export function isFeatureValidByMarketAvailabilty(
  variable: IConfigurationVariable
): boolean | undefined {
  const assignedValue = variable?.values.find((value) => value.assigned);
  switch (variable.variableType) {
    case EVariableType.String:
      return !!assignedValue?.value || isValidByMarketAvailability(variable);
    case EVariableType.Submodel:
    case EVariableType.Number:
      return (
        (assignedValue?.value != null &&
          typeof assignedValue.value === 'number' &&
          assignedValue.value > 0) ||
        isValidByMarketAvailability(variable)
      );
    case EVariableType.Singleselect:
    case EVariableType.Multiselect:
      return (
        variable.values.length > 0 &&
        (variable.distinctValueCount ?? 0) > 0 &&
        isValidByMarketAvailability(variable) &&
        isOptionValidByMarketAvailability(variable)
      );
    default:
      return true;
  }
}

/**
 * Validates options within a multi/single-select variable based on PMA and RMA properties.
 * Sets the suppressed property on all option values and checks if at least one option is valid.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {boolean} True if at least one option has valid market availability, false otherwise.
 */
export function isOptionValidByMarketAvailability(variable: IConfigurationVariable): boolean {
  AddAttributeToValue(variable, EVariableAttribute.Suppress);
  const hasValueMarketAvailability = variable.values.some((value) => !value.suppressed);
  return hasValueMarketAvailability;
}

/**
 * Checks if a variable or value is valid based on RMA and PMA properties.
 * Both ProcosMarketAvailable and RulesMarketAvailable must be true for validity.
 * @param {IConfigurationVariable | IConfigurationValue} variable - The configuration variable or value.
 * @returns {boolean} True if both RMA and PMA properties are true, false otherwise.
 */
export function isValidByMarketAvailability(
  variable: IConfigurationVariable | IConfigurationValue
): boolean {
  return (
    getPropertyValue<boolean>(
      variable,
      EConfigitProperty.ProcosMarketAvailable,
      EVariableType.Boolean
    ) &&
    getPropertyValue<boolean>(
      variable,
      EConfigitProperty.RulesMarketAvailable,
      EVariableType.Boolean
    )
  );
}

/**
 * Retrieves a property value from a variable or value's properties array.
 * Returns typed values based on the specified type (Boolean, Number, or String).
 * @template T - The expected return type (string, number, or boolean).
 * @param {IConfigurationVariable | IConfigurationValue | null} variable - The variable or value containing properties.
 * @param {string} property - The property ID to retrieve.
 * @param {string} type - The expected type ('Boolean', 'Number', or 'String').
 * @returns {T} The property value cast to type T, or default value (false/0/'') if not found.
 */
export function getPropertyValue<T extends string | number | boolean>(
  variable: IConfigurationVariable | IConfigurationValue | null,
  property: string,
  type: string
): T {
  const value = variable?.properties?.find((varProperty) => varProperty.id === property)?.value;
  if (type === EVariableType.Boolean) {
    return (value ?? false) as T;
  }
  if (type === EVariableType.Number) {
    return (value ?? 0) as T;
  }
  return (value ?? '') as T;
}

/**
 * Determines the initial tab index for a list of sections.
 * Finds the first non-suppressed section to use as the initial tab.
 * @param {ISection[]} sections - The list of sections.
 * @returns {number} The index of the first non-suppressed section, or 0 if all are suppressed.
 */
export function getInitialTab(sections: ISection[]): number {
  const initialTab = sections?.findIndex((s) => !s.isItemsAvailable);
  if (initialTab !== -1) {
    return initialTab;
  }
  return 0;
}

/**
 * Determines the initial tab index for a submodel configuration.
 * Gets selectable sections for the model path and finds the first non-suppressed section.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @param {string} modelPath - The submodel path.
 * @returns {number} The initial tab index for the submodel.
 */
export function getSubModelInitialTab(
  configuration: IExtendedConfigureResponse,
  modelPath: string
): number {
  const { sections } = getSelectableSections(configuration, modelPath);
  return getInitialTab(sections);
}

/**
 * Adds a specified attribute to all values in a variable.
 * Supports adding 'Suppress' or 'Hide' attributes based on property values.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @param {string} attribute - The attribute to add ('Suppress' or 'Hide').
 * @returns {void}
 */
export function AddAttributeToValue(variable: IConfigurationVariable, attribute: string): void {
  variable?.values.forEach((value: IConfigurationValue) => {
    switch (attribute) {
      case EVariableAttribute.Suppress:
        value.suppressed = !isValidByMarketAvailability(value);
        break;
      case EVariableAttribute.Hide:
        value.hide = getPropertyValue<boolean>(
          value,
          EConfigitProperty.Hide,
          EVariableType.Boolean
        );
        break;
    }
  });
}

/**
 * Retrieves the current product model ID (root model or active submodel).
 * Parses the model path from URL to determine the active model.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @returns {string} The current product model ID.
 */
export function getCurrentModel(configuration: IExtendedConfigureResponse): string {
  let currentModel = '';
  if (configuration.savedConfiguration) {
    const { rootModel } = configuration.savedConfiguration.modelContext;
    const modelPath = UrlHelper.getSearchParameter(EUrlParams.Model);
    const path = modelPath ? [rootModel.id, ...modelPath.split('.')] : [rootModel.id];
    currentModel = path.at(-1)!;
  }
  return currentModel;
}

/**
 * Retrieves a value (price or sales text) from a nested Map structure using an ID key.
 * Searches through the outer and inner maps to find the matching entry.
 * @param {Map<string, Map<string, number | string>>} mapData - The nested map structure.
 * @param {string} id - The ID key to search for.
 * @returns {number | string | null} The found value or null if not found.
 */
export function getValueById<T = number | string>(
  mapData: Map<string, Map<string, T>>,
  id?: string
): T | null {
  if (mapData.size > 0) {
    for (const [_outerKey, innerMap] of mapData) {
      if (id && innerMap.has(id)) {
        return innerMap?.get(id) ?? null;
      }
    }
  }
  return null;
}

/**
 * Checks if a variable has the IsPassword (ISP) property set to true.
 * Password-protected features have special handling in the UI.
 * @param {IConfigurationVariable | null} variable - The configuration variable.
 * @returns {boolean | undefined} True if the variable is password-protected, false or undefined otherwise.
 */
export function checkIsPasswordFeature(
  variable: IConfigurationVariable | null
): boolean | undefined {
  return getPropertyValue<boolean>(variable, EConfigitProperty.IsPassword, EVariableType.Boolean);
}

/**
 * Checks if any removed assignment in a conflict has the IsPassword property set to true.
 * Used to determine if password-protected features are involved in conflicts.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @returns {IVariableAssignment | undefined} The password-protected variable assignment or undefined if none found.
 */
export function checkPasswordFeatureInConflict(
  configuration: IExtendedConfigureResponse
): IVariableAssignment | undefined {
  return configuration.conflict?.assignmentsToRemove.conflicts?.find((removeAssignment) =>
    checkIsPasswordFeature(getVariableFromAllSections(configuration, removeAssignment.variable.id))
  );
}

/**
 * Fetches marketing information (sales text, price, images, property associations) for a submodel.
 * Only executes if the model differs from the current product ID.
 * Triggered when clicking the configure icon or breadcrumb navigation.
 * @param {ISubModelDispatchProps} param - Object containing token, model ID, and dispatch function.
 * @returns {Promise<void>}
 */
export async function getSubmodelMarketingInfo(param: ISubModelDispatchProps): Promise<void> {
  const { token, model, dispatch } = param;

  if (model && model !== getProductId()) {
    const configuration = AppStore.getState().configuration;
    const applicationSettings = AppStore.getState().applicationSettings;
    const modelId = model;
    const language = getLanguageCode();

    dispatch(
      getPropertyAssociations({
        token: token,
        packageCode: getProductId(),
        productCode: modelId,
        propertyDefiniton: configuration.propertyDefinition,
      })
    );

    // Call getSalesText and getPrice in parallel
    dispatch(
      getShortSalesTextAndPrice({
        salesTextShort: {
          language,
          token,

          showError: false,
        },

        price: {
          token,
          countryCode: applicationSettings.priceListMap,
          currencyCode: applicationSettings.currency.currencyCode,
        },
        modelId,
      })
    );

    dispatch(getProductImages({ token: token, modelId }));

    dispatch(
      getSalesText({ language, token, modelId, apiType: ESalesTextType.Long, showError: false })
    );
  }
}

/**
 * Extracts all parent IDs for a submodel from its hierarchical ID.
 * Parses the submodel ID to build an array of parent model IDs including the root.
 * @param {string} submodelId - The current submodel ID (e.g., "I_722467.I_830230.I_867030.867043_C").
 * @param {string} rootProductId - The root product ID.
 * @returns {string[]} Array of parent IDs starting from root to immediate parent of the submodel.
 */
export function getSubModelParents(submodelId: string, rootProductId: string): string[] {
  const parentIds = [rootProductId];

  //split the submodel id by dot(.) and remove the _I and _C to get the parents id array
  //ex:(I_722467.I_830230.I_867030.867043_C) --> [722467,830230,867030,867043]
  const splitValues = submodelId.split('.');
  const subModelIds = splitValues.map((value) => value.replace('I_', '')?.replace('_C', ''));

  //skip the current submodel id from the list
  //ex:[722467,830230,867030,867043] --> [722467,830230,867030]
  subModelIds?.pop();

  return [...parentIds, ...subModelIds];
}

/**
 * Determines the type of a section based on its ID structure.
 * Identifies multi-numeric sections by checking for multiple underscore-separated parts.
 * @param {ISection} section - The current section.
 * @returns {ESectionInfoType} The section type (MultiNumeric or General).
 */
function getSectionType(section: ISection): ESectionInfoType {
  const sectionId = section.id?.substring(section.id.lastIndexOf('.') + 1);
  const type =
    sectionId.split('_').length > 2 ? ESectionInfoType.MultiNumeric : ESectionInfoType.General;
  return type;
}

/**
 * Determines if a variable should be displayed in the guarded section.
 * Checks if the variable is service-relevant and visible (not hidden or suppressed).
 * Only applicable for service configurations.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {boolean} True if the variable should appear in the guarded section, false otherwise.
 */
function showVariableInGuardedSection(variable: IConfigurationVariable): boolean {
  const serviceRelevant = variable?.pdmProperties?.serviceRelevant;
  return serviceRelevant && isVariableVisible(variable);
}

/**
 * Checks if a variable should be visible in the UI.
 * A variable is visible if it's not hidden and not suppressed.
 * @param {IConfigurationVariable} variable - The configuration variable.
 * @returns {boolean} True if the variable should be visible, false if it should be hidden.
 */
export function isVariableVisible(variable: IConfigurationVariable | IConfigurationValue): boolean {
  return !variable.hide && !variable.suppressed;
}

/**
 * Creates a React element with the label and an incomplete indicator if needed.
 * Adds a 'required' CSS class when the field is incomplete.
 * @param {string} label - The label text.
 * @param {boolean} isComplete - Whether the field is complete.
 * @returns {ReactElement} A React span element with the label and appropriate styling.
 */
export function getLabelWithInCompleteIcon(label: string, isComplete: boolean): ReactElement {
  return createElement('span', { className: `${isComplete === false ? 'required' : ''}` }, label);
}

/**
 * Checks if the given feature code ends with the LessFeature suffix
 * @param {string} code Feature or option code to check
 * @returns {boolean} True if the code ends with the LessFeature suffix, false otherwise
 */
export function isLessFeature(code: string): boolean {
  return code.endsWith(IdFormat.Suffix.LessFeature);
}

/**
 * Creates a delete promise with toast notifications for configuration deletion.
 * Handles success and error cases with appropriate toast messages.
 * Updates Redux state on successful deletion.
 * @param {IMyConfiguration} config - The configuration to delete.
 * @param {AppDispatch} dispatch - Redux dispatch function.
 * @param {TFunction} t - i18n translation function.
 * @returns {() => void} A function to be called when deletion is confirmed.
 */
export const deleteConfigurationHelper = (
  config: IMyConfiguration,
  dispatch: AppDispatch,
  t: TFunction
): (() => void) => {
  return () => {
    // Create an initial toast showing the "in progress" message
    const toastId = initiateToast(
      t('errorMessages.general.deletionInProgress', { configName: config.name })
    );

    // Dispatch the delete action
    dispatch(deleteConfiguration({ configurationId: config.configurationId }))
      .then((result: any) => {
        if (
          deleteConfiguration.fulfilled.match(result) &&
          result?.payload &&
          typeof result.payload.data === 'string'
        ) {
          // Update Redux directly
          dispatch(removeConfigurationById(config.configurationId));

          // Update toast to show success
          updateToast({
            toastId,
            type: EMessageType.Success,
            message: t('successMessages.configurationDeleted', { configName: config.name }),
            autoClose: 3000,
          });
        } else if (result?.payload?.data?.response?.data?.isSuccessful === false) {
          const errorId = result.payload.data.response.data.errorId;

          // Check if we have a specific error message for this error ID
          let errorMessage;
          if (errorId) {
            errorMessage = t(`errorMessages.general.${errorId}`, { configName: config.name });
          } else {
            // Fall back to the message from the API
            errorMessage = result.payload.data.response.data.message;
          }

          // Update toast to show error
          updateToast({
            toastId,
            type: EMessageType.Error,
            message: errorMessage,
            autoClose: 5000,
          });
        } else {
          // Update toast to show generic error
          updateToast({
            toastId,
            type: EMessageType.Error,
            message: t('errorMessages.http.500'),
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        // Update toast to show error from catch block
        updateToast({
          toastId,
          type: EMessageType.Error,
          message: error.message || t('errorMessages.http.500'),
          autoClose: 5000,
        });
      });
  };
};

/**
 * Checks if a configuration state is in a restricted state.
 * Restricted states prevent certain actions on the configuration.
 * @param {string} state - The configuration state to check.
 * @returns {boolean} True if the state is restricted, false otherwise.
 */
export const isRestrictedState = (state: string): boolean => {
  if (!state) {
    return false;
  }
  return RestrictedStates.includes(state as EConfigurationType);
};

/**
 * Checks if the current access level is read-only.
 * Read-only access prevents modifications to the configuration.
 * @param {string} access - The access level to check.
 * @returns {boolean} True if access is read-only, false otherwise.
 */
export const isReadOnlyAccess = (access: string): boolean => {
  return access === EConfigurationType.ReadOnly;
};

export const isTempConfiguration = (state: string): boolean => {
  return state === EConfigurationType.Temp;
};

/**
 * Gets the last assignment information for display purposes
 * @description Retrieves and processes the last assignment variable and value from configuration
 * to determine the appropriate display value. Handles special cases like boolean values and
 * provides fallback logic for edge cases.
 * @param {IExtendedConfigureResponse} configuration - The current configuration state containing assignment data
 * @returns {IConfigurationValue | IValueType | undefined} The processed assignment value for display:
 * - IConfigurationValue if exact match found in variable values
 * - IValueType (raw value) if no exact match or as fallback
 * - undefined if no configuration or last change data available
 */
export const getLastAssignmentInfo = (
  configuration: IExtendedConfigureResponse
): IConfigurationValue | IValueType | undefined => {
  const lastAssignmentVariable = getLastAssignmentVariable(configuration);
  const lastChangeValue = configuration.lastChange?.value;

  if (!lastAssignmentVariable || !lastChangeValue) {
    return lastChangeValue;
  }

  // First, try to find exact value match
  const exactMatch = lastAssignmentVariable.values?.find((v) => v.value === lastChangeValue);

  if (exactMatch) {
    return exactMatch;
  }

  // Handle special case: boolean true value with single option
  if (lastChangeValue === true && lastAssignmentVariable.values?.length === 1) {
    return lastAssignmentVariable.values[0];
  }

  // Fallback to the raw value
  return lastChangeValue;
};

/**
 * Assigns properties (suppressed, isItemsAvailable, type,price,short salestext) to all sections and variables in the array.
 * Adds Guarded section if it's a service configuration.
 * Adds isComplete property to configuration.
 * @param {IExtendedConfigureResponse} configuration - The configuration state.
 * @returns {IConfiguration} The configuration data with updated price values.
 */
export function setAttributesToSectionsAndVariables(
  configuration: IExtendedConfigureResponse
): IConfiguration {
  if (configuration.data?.sections) {
    setAttributesToSections(configuration.data.sections, configuration);
    configuration.isDataLoaded = true;
  }
  setIsCompleteConfiguration(configuration);

  return configuration.data;
}

/**
 * Assigns properties (suppressed, isItemsAvailable, type,price,short salestext) to all sections and variables in the array.
 * @param {ISection[]} sections - Array of section objects.
 * @param {IExtendedConfigureResponse} state - The configuration state.
 * @returns {void} Mutates sections in place.
 */
/** Returns the tooltip for a feature or option, handling readOnly and bundle cases. */
export function getFeatureTooltip(
  variable: IConfigurationVariable | IConfigurationValue,
  isReadOnly: boolean,
  showCode?: boolean
): string {
  const name = getNameWithCode(variable as IFeatureOrFamily, showCode);
  if (variable.bundleInfo?.isBundled) {
    const bundleText = t('tooltip.bundleDrivenNamed', {
      bundleName: variable.bundleInfo.bundleDrivingCode,
    });
    return `${name}\n\n${bundleText}`;
  }
  if (isReadOnly) {
    return t('tooltip.readOnlyTooltipText');
  }
  if ('variableType' in variable && variable.variableType === EVariableType.String) {
    return '';
  }
  return name;
}

export function setAttributesToSections(
  sections: ISection[],
  state: IExtendedConfigureResponse
): void {
  for (const section of sections) {
    section.variables = section.variables.filter((sec) => sec.id !== ModelInclusion.Id);
    updateVariableAttributes(section.variables, state);
    if (section.sections?.length) {
      setAttributesToSections(section.sections, state);
    }
  }
  assignPropertiesToSections(sections, state);
}

/**
 * Checks whether a composite driven code is active in any bundle's driven codes array.
 *
 * selectedBundles is keyed by fully-qualified driving code paths and each value is a string[]
 * of composite driven codes included for free when that driving code is assigned.
 * This function searches across all driven code arrays.
 *
 * @param {IExtendedConfigureResponse} config - The current configuration response.
 * @param {string} drivenCode - The composite driven code to look up
 *   (e.g. 'FNA4884.NNAL591' for Singleselect, or variable.id for Number/String/Date).
 * @returns {string | undefined} The driving code path (selectedBundles key) if the driven code is active, otherwise undefined.
 *
 * Examples:
 *   drivenCode = '989801296553'  → returns the driver key if it is a driven code in any active bundle
 *   drivenCode = 'UNKNOWN'       → returns undefined
 */
function getBundleDrivingCodeFromDrivenCode(
  config: IExtendedConfigureResponse,
  drivenCode: string
): string | undefined {
  return Object.entries(config.selectedBundles).find(([, driven]) =>
    driven.includes(drivenCode)
  )?.[0];
}

/**
 * Updates the price and isBundled flag on a variable based on its PDM price-relevance and bundle state.
 * Extracted from updateVariableAttributes to keep cognitive complexity within limits.
 * If price is not relevant or no price map is available, sets price to null.
 * If the variable's id matches an active driven code in selectedBundles, sets price to 0 and marks isBundled.
 * Otherwise looks up the catalog price from the price map using the leaf variableId.
 * @param {IConfigurationVariable} variable - The variable to update.
 * @param {string} variableId - The leaf segment of the variable ID (last part after the final dot).
 * @param {IExtendedConfigureResponse} config - The current configuration state.
 * @returns {void}
 */
function updateVariablePrice(
  variable: IConfigurationVariable,
  variableId: string,
  config: IExtendedConfigureResponse
): void {
  const isPriceRelevant = variable?.pdmProperties?.priceRelevant ?? false;
  if (!isPriceRelevant || !config.price) {
    variable.price = null;
    return;
  }
  const drivingCode = getBundleDrivingCodeFromDrivenCode(config, variable.id);
  if (drivingCode) {
    variable.bundleInfo = { isBundled: true, bundleDrivingCode: drivingCode };
    variable.price = 0;
  } else {
    variable.price = getValueById(config.price, variableId);
  }
}

/**
 * Updates all variables and their values with price information.
 * Assigns properties  type,price,short salestext to all variables.
 * @param {IConfigurationVariable[]} variables - Array of configuration variables.
 * @param {IExtendedConfigureResponse} config - The configuration state.
 * @returns {void} Mutates variables in place.
 */
function updateVariableAttributes(
  variables: IConfigurationVariable[],
  config: IExtendedConfigureResponse
): void {
  if (!variables?.length) {
    return;
  }

  for (const variable of variables) {
    const variableType = getVariableType(variable);
    variable.variableType = variableType as keyof typeof EVariableType;

    const idParts = variable.id.split('.');
    const variableId = idParts.at(-1)!;
    const featureCode =
      variableType === EVariableType.Submodel ? variableId.split('_')[0] : variableId;
    //Update short sales text
    variable.shortSalesText =
      appSettings.UseShortSalesText && config.shortSalesText
        ? getValueById(config.shortSalesText, featureCode)
        : null;

    updateVariablePrice(variable, variableId, config);
    updateVariableGuardedStatus(variable, config);
    // Update multi/single select options
    if (variableType === EVariableType.Multiselect || variableType === EVariableType.Singleselect) {
      setPropertiesToSingleAndMultiValuedOptions(variable, config);
    }

    setSuppressedToVariables(variable);
    setIsCompleteToVariables(variable, config.savedConfiguration?.optionalItems);
  }
}

/**
 * Add isGuarded property to variable if configuration is service configuration and variable is service relevant
 * @param {IConfigurationVariable} variable - The variable to update.
 * @param {IExtendedConfigureResponse} config - The configuration state.
 * @returns {void}
 */
function updateVariableGuardedStatus(
  variable: IConfigurationVariable,
  config: IExtendedConfigureResponse
): void {
  if (
    config?.savedConfiguration?.isServiceConfiguration &&
    !isSubmodelCountVariable(variable) &&
    showVariableInGuardedSection(variable)
  ) {
    variable.isGuarded = true;
  }
}

/**
 * Extracts the code from a multi-numeric section id.
 * Example: id = I_867030.S867030_FUS8320_Accessories_998 → FUS8320_Accessories
 * @param {string} id - Section id
 * @returns {string} The multi-numeric section code
 */
export function getMultiNumericCode(id: string): string {
  //To get the actual feature instead of whole heirarchy
  //Ex: id = I_867030.S867030_FUS8320_Accessories_998 ---> S867030_FUS8320_Accessories_998
  id = id?.substring(id.lastIndexOf('.') + 1);

  //Get Multi-numeric feature id
  //Ex: id = S867030_FUS8320_Accessories_998 ---> FUS8320_Accessories
  const splitMultiNumericId = id?.split('_');
  const multiNumericId = splitMultiNumericId.slice(1, -1);
  return multiNumericId.join('_');
}
